<template>
  <div class="blog-view-container">
    <v-container class="blog-content-area">
      <v-row justify="center" style="padding-top: 60px" class="mt-12 mb-8">
        <v-col cols="12" class="text-center">
          <div class="section-header-wrapper">
            <div class="section-accent-line"></div>
            <h2 class="section-heading">Latest From Oh Yes Blog</h2>
            <div class="section-accent-line"></div>
          </div>
        </v-col>
      </v-row>

      <v-row justify="center" class="mb-12">
        <v-col cols="12" class="text-center">
          <div class="category-filters-wrapper">
            <div class="category-filters">
              <v-btn
                v-for="cat in categories"
                :key="cat"
                @click="selectedCategory = cat"
                elevation="0"
                class="mx-2 my-2 filter-button"
                :class="{ 'active-filter': activeCategory === cat }"
              >
                <span class="filter-text">{{ cat }}</span>
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>

      <v-row class="blog-grid">
        <v-col
          v-for="(post, index) in filteredPosts"
          :key="index"
          cols="12"
          sm="6"
          lg="4"
          class="d-flex blog-col"
          @click="navigateToBlog(post.title)"
        >
          <v-card class="blog-card mx-auto" max-width="400" elevation="0">
            <div class="card-wrapper">
              <div class="image-wrapper">
                <v-img
                  :src="getOptimizedImage(post.imageUrl, 800, 72)"
                  :lazy-src="getOptimizedImage(post.imageUrl, 120, 35)"
                  height="240px"
                  class="blog-image"
                >
                  <div class="image-overlay"></div>
                </v-img>
              </div>

              <div class="card-content">
                <v-card-title class="post-title px-6 pt-5 pb-3">
                  {{ post.title }}
                </v-card-title>

                <v-card-text class="post-excerpt px-6 pb-4">
                  {{ post.excerpt }}
                </v-card-text>

                <v-card-actions class="px-6 pb-5 pt-2">
                  <v-spacer></v-spacer>
                  <v-btn text small class="read-more-btn">
                    <span>Read More</span>
                    <v-icon right small class="arrow-icon"
                      >mdi-arrow-right</v-icon
                    >
                  </v-btn>
                </v-card-actions>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row justify="center" class="my-12">
        <v-btn
          v-if="hasMorePosts"
          outlined
          class="load-more-btn"
          @click="loadMore"
        >
          <span class="load-more-text">Load More Posts</span>
          <v-icon right class="chevron-icon">mdi-chevron-down</v-icon>
        </v-btn>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import blogData from "../../data/blog.js";
import { filterBlogsForRegion } from "@/utils/blogRegion";

export default {
  name: "BlogView",
  data() {
    const { blogPosts } = blogData;
    return {
      selectedCategory: "AC Cleaning",
      postsPerLoad: 6,
      currentPostCount: 6,
      fullBlogPosts: blogPosts,
    };
  },
  watch: {
    selectedCategory() {
      this.currentPostCount = this.postsPerLoad;
    },
  },
  computed: {
    availablePosts() {
      return filterBlogsForRegion(this.fullBlogPosts, this.isUSRoute);
    },
    categories() {
      return [...new Set(this.availablePosts.map((post) => post.category))];
    },
    activeCategory() {
      return this.categories.includes(this.selectedCategory)
        ? this.selectedCategory
        : this.categories[0];
    },
    categoryPosts() {
      return this.availablePosts.filter(
        (post) => post.category === this.activeCategory,
      );
    },
    filteredPosts() {
      return this.categoryPosts.slice(0, this.currentPostCount);
    },
    hasMorePosts() {
      return this.categoryPosts.length > this.currentPostCount;
    },
    isUSRoute() {
      return (
        this.$route.meta?.location === "us" ||
        this.$route.path.startsWith("/us")
      );
    },
  },
  methods: {
    getOptimizedImage(url, width = 800, quality = 72) {
      if (!url || typeof url !== "string") return url;

      // Only transform ImageKit URLs; keep all other providers unchanged.
      const match = url.match(/^(https:\/\/ik\.imagekit\.io\/[^/]+\/)(.+)$/);
      if (!match) return url;

      const base = match[1];
      const path = match[2].replace(/^tr:[^/]+\//, "");
      return `${base}tr:w-${width},q-${quality},f-auto/${path}`;
    },

    loadMore() {
      this.currentPostCount += this.postsPerLoad;
    },
    navigateToBlog(title) {
      this.$router.push({
        name: this.isUSRoute ? "BlogDetailsUS" : "BlogDetails",
        params: { blogTitle: title },
      });
    },
  },
};
</script>

<style scoped>
.blog-view-container {
  background-color: #ffffff;
  min-height: 100vh;
}

.blog-hero-banner {
  background: linear-gradient(135deg, #ffffff 0%, #fef5f1 100%);
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid #f0f0f0;
}

.hero-gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
    circle at top right,
    rgba(255, 107, 53, 0.08) 0%,
    transparent 60%
  );
  pointer-events: none;
}

.position-relative {
  position: relative;
  z-index: 1;
}

/* Hero Banner */
.animate-fade-in {
  animation: fadeInUp 0.8s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.hero-title {
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: 900;
  background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 16px;
  line-height: 1.1;
  letter-spacing: -2px;
}

.banner-subtitle {
  font-size: clamp(1rem, 2vw, 1.25rem);
  color: #666;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  font-weight: 400;
}

.decorative-line {
  width: 100px;
  height: 3px;
  background: linear-gradient(90deg, transparent, #ff6b35, transparent);
  margin: 0 auto;
  border-radius: 2px;
  opacity: 0.6;
}

/* Section Header */
.section-header-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  margin-bottom: 10px;
}

.section-accent-line {
  width: 60px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #ff6b35);
}

.section-accent-line:last-child {
  background: linear-gradient(90deg, #ff6b35, transparent);
}

.section-heading {
  font-size: 64px;
  font-weight: 900;
  background: #f46523;
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 16px;
  line-height: 1.1;
  animation: gradientShift 8s ease infinite;
  letter-spacing: -1.5px;
}

/* Category Filters */
.category-filters-wrapper {
  background: #ffffff;
  border-radius: 50px;
  padding: 16px 24px;
  display: inline-block;
  border: 2px solid #f0f0f0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.category-filters {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
}

.v-btn.filter-button {
  font-weight: 600;
  border-radius: 25px;
  padding: 0 28px !important;
  height: 44px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-transform: none !important;
  background-color: #f8f8f8 !important;
  color: #666 !important;
  border: 2px solid #e8e8e8 !important;
  position: relative;
  overflow: hidden;
}

.filter-text {
  position: relative;
  z-index: 2;
  font-size: 0.95rem;
}

.v-btn.filter-button::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 107, 53, 0.1);
  transition:
    width 0.4s,
    height 0.4s,
    top 0.4s,
    left 0.4s;
  transform: translate(-50%, -50%);
}

.v-btn.filter-button:hover:not(.active-filter)::before {
  width: 300px;
  height: 300px;
}

.v-btn.filter-button:hover:not(.active-filter) {
  border-color: rgba(255, 107, 53, 0.3) !important;
  color: #ff6b35 !important;
}

.v-btn.active-filter {
  background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%) !important;
  color: white !important;
  border-color: #ff6b35 !important;
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3) !important;
  transform: translateY(-2px);
}

/* Blog Card */
.blog-grid {
  margin: 0 -12px;
}

.blog-col {
  padding: 12px;
}

.blog-card {
  background: #ffffff !important;
  border-radius: 20px !important;
  border: 1px solid rgba(244, 101, 35, 0.15);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.card-wrapper {
  position: relative;
}

.blog-card::before {
  content: "";
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border-radius: 20px;
  background: linear-gradient(135deg, #ff6b35, #ff8c42);
  opacity: 0;
  transition: opacity 0.4s;
  z-index: -1;
}

.blog-card:hover {
  transform: translateY(-8px);
  border-color: rgba(255, 107, 53, 0.3);
  box-shadow: 0 12px 40px rgba(255, 107, 53, 0.15);
}

.blog-card:hover::before {
  opacity: 0.1;
}

.image-wrapper {
  position: relative;
  overflow: hidden;
  border-radius: 18px 18px 0 0;
}

.blog-image {
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 18px 18px 0 0;
}

.blog-card:hover .blog-image {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(255, 107, 53, 0.15) 100%
  );
  opacity: 0;
  transition: opacity 0.4s;
}

.blog-card:hover .image-overlay {
  opacity: 1;
}

.card-content {
  background: #ffffff;
}

.post-title {
  font-size: 1.25rem;
  line-height: 1.35;
  font-weight: 700;
  color: #1a1a1a !important;
  min-height: 96px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  white-space: normal;
  padding-right: 6px;
  margin: 0;
}
.post-excerpt {
  font-size: 0.95rem;
  color: #666 !important;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 105px;
  line-height: 1.6;
  word-break: break-word;
  margin-top: 6px;
}

.read-more-btn {
  color: #ff6b35 !important;
  font-weight: 700;
  text-transform: none !important;
  letter-spacing: 0.3px;
  transition: all 0.3s ease;
  padding: 8px 16px !important;
  border-radius: 8px;
}

.read-more-btn:hover {
  background: rgba(255, 107, 53, 0.1) !important;
}

.read-more-btn .arrow-icon {
  transition: transform 0.3s ease;
}

.read-more-btn:hover .arrow-icon {
  transform: translateX(5px);
}

/* Load More Button */
.load-more-btn {
  background: #ffffff !important;
  border: 2px solid #ff6b35 !important;
  color: #ff6b35 !important;
  font-weight: 700;
  padding: 16px 40px !important;
  height: auto !important;
  border-radius: 30px;
  text-transform: none !important;
  letter-spacing: 0.5px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.15);
}

.load-more-btn:hover {
  background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%) !important;
  color: white !important;
  border-color: #ff6b35 !important;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(255, 107, 53, 0.3);
}

.load-more-text {
  font-size: 1rem;
}

.chevron-icon {
  transition: transform 0.3s ease;
  color: inherit !important;
}

.load-more-btn:hover .chevron-icon {
  transform: translateY(3px);
  animation: bounce 0.6s ease-in-out infinite;
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(3px);
  }
  50% {
    transform: translateY(6px);
  }
}

@media (max-width: 960px) {
  .section-header-wrapper {
    gap: 15px;
  }

  .section-accent-line {
    width: 40px;
  }

  .category-filters-wrapper {
    padding: 12px 20px;
  }

  .hero-title {
    font-size: 2.5rem;
  }
}

@media (max-width: 600px) {
  .section-header-wrapper {
    flex-direction: column;
    gap: 10px;
  }

  .section-accent-line {
    width: 60px;
    height: 2px;
  }

  .section-accent-line:first-child {
    background: linear-gradient(180deg, transparent, #ff6b35);
  }

  .section-accent-line:last-child {
    background: linear-gradient(180deg, #ff6b35, transparent);
  }

  .hero-title {
    font-size: 2rem;
    letter-spacing: -1px;
  }

  .banner-subtitle {
    font-size: 1rem;
  }

  .category-filters-wrapper {
    padding: 12px 16px;
  }

  .v-btn.filter-button {
    padding: 0 20px !important;
    height: 40px !important;
  }

  .post-title {
    font-size: 1.15rem;
  }
}
</style>
